// App.js
import { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { auth, logoutUser, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, getDocs, collection, query, where } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import Auth from "./components/Auth";
import Dashboard from "./components/Dashboard";
import GuestManager from "./components/GuestManager";
import TaskManager from "./components/TaskManager";
import ReportExport from "./components/ReportExport";
import SeatingManager from "./components/SeatingManager";
import Profile from "./components/Profile";
import TutorialModal from "./components/TutorialModal";
import AdSenseAutoAds from "./components/AdSenseAutoAds";
import Vendors from "./components/Vendors";
import AdminVendors from "./components/AdminVendors";

const ADMIN_EMAIL = 'ptrakkides@gmail.com';

function App() {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const loginNameRef = useRef(null); // name provided by onLogin — skip Firestore fetch if set

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
      if (!firebaseUser) { setUserName(''); loginNameRef.current = null; return; }

      // onLogin already fetched and set the name — don't override it
      if (loginNameRef.current) { loginNameRef.current = null; return; }

      try {
        const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
        const name = snap.exists() ? snap.data().displayName : null;
        setUserName(name || firebaseUser.displayName || firebaseUser.email);
      } catch (_) {
        setUserName(firebaseUser.displayName || firebaseUser.email);
      }
    });
    return () => unsubscribe();
  }, []);

  // Restore last selected event on refresh/mount
  useEffect(() => {
    if (!user) return;
    const savedId = localStorage.getItem(`tenekkapp_last_event_${user.uid}`);
    if (!savedId) return;

    const restoreEvent = async () => {
      try {
        // Try owned event first
        const ownedSnap = await getDoc(doc(db, 'users', user.uid, 'events', savedId));
        if (ownedSnap.exists()) {
          setCurrentEvent({ id: ownedSnap.id, ...ownedSnap.data() });
          return;
        }
        // Try shared event
        const sharedSnap = await getDocs(
          query(collection(db, 'users', user.uid, 'sharedEvents'), where('eventId', '==', savedId))
        );
        if (!sharedSnap.empty) {
          const sharedData = sharedSnap.docs[0].data();
          const eventSnap = await getDoc(doc(db, 'users', sharedData.ownerId, 'events', savedId));
          if (eventSnap.exists()) {
            setCurrentEvent({
              id: eventSnap.id,
              ...eventSnap.data(),
              ownerId: sharedData.ownerId,
              isShared: true,
              sharedBy: sharedData.ownerEmail,
              userRole: sharedData.role,
            });
          }
        }
      } catch (e) {
        // Silently fail — Dashboard's autoSelect will handle it
      }
    };

    restoreEvent();
  }, [user]);

  // Show tutorial on first login
  useEffect(() => {
    if (!user) return;
    const key = `tenekkapp_tour_done_${user.uid}`;
    if (!localStorage.getItem(key)) setShowTutorial(true);
  }, [user]);

  const completeTutorial = () => {
    localStorage.setItem(`tenekkapp_tour_done_${user.uid}`, '1');
    setShowTutorial(false);
  };

  const handleLogout = async () => {
    await logoutUser();
    setUser(null);
    setCurrentEvent(null);
  };

  const toggleLanguage = () => {
    const newLanguage = i18n.language === "en" ? "el" : "en";
    i18n.changeLanguage(newLanguage);
    localStorage.setItem("language", newLanguage);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="d-flex flex-column align-items-center">
          <img
            src="/logo.png"
            alt="Tenekkapp Logo"
            height="60"
            className="mb-3"
            style={{ filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.1))" }}
          />
          <div
            className="spinner-border text-primary mb-3"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">{t("common.loading")}</span>
          </div>
          <h4 className="text-primary fw-bold">{t("common.loading_app")}</h4>
          <p className="text-muted">{t("common.please_wait")}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Router>
        <AdSenseAutoAds />
        <Auth onLogin={(user, name) => { setUser(user); if (name) { loginNameRef.current = name; setUserName(name); } }} />
      </Router>
    );
  }

  return (
    <Router>
      <AdSenseAutoAds />
      <div className="min-vh-100" style={{ background: "var(--bg-primary)" }}>
        <nav className="navbar navbar-expand-lg navbar-dark shadow-lg border-0">
          <div className="container">
            <Link className="navbar-brand d-flex align-items-center" to="/">
              <img src="/logo.png" alt="Tenekkapp Logo" height="40" className="me-2" />
              <span className="fw-bold fs-4 text-white">{t("common.appName")}</span>
            </Link>
            <button
              className="navbar-toggler border-0"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarNav"
            >
              <span className="navbar-toggler-icon"></span>
            </button>
            <div className="collapse navbar-collapse" id="navbarNav">
              <ul className="navbar-nav ms-auto">
                <li className="nav-item">
                  <Link className="nav-link fw-semibold px-3" to="/">
                    <i className="fas fa-tachometer-alt me-1"></i>
                    {t("nav.dashboard")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link fw-semibold px-3" to="/guests">
                    <i className="fas fa-users me-1"></i>
                    {t("nav.guests")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link fw-semibold px-3" to="/tasks">
                    <i className="fas fa-tasks me-1"></i>
                    {t("nav.tasks")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link fw-semibold px-3" to="/seating">
                    <i className="fas fa-chair me-1"></i>
                    {t("nav.seating")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link fw-semibold px-3" to="/report">
                    <i className="fas fa-chart-bar me-1"></i>
                    {t("nav.report")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link fw-semibold px-3" to="/vendors">
                    <i className="fas fa-store me-1"></i>
                    {t("nav.vendors")}
                  </Link>
                </li>
                {user.email === ADMIN_EMAIL && (
                  <li className="nav-item">
                    <Link className="nav-link fw-semibold px-3" to="/admin">
                      <i className="fas fa-cogs me-1"></i>
                      {t("nav.admin")}
                    </Link>
                  </li>
                )}
                <li className="nav-item">
                  <Link className="nav-link fw-semibold px-3" to="/profile">
                    <i className="fas fa-user me-1"></i>
                    {t("nav.profile")}
                  </Link>
                </li>
              </ul>
              <div className="ms-3 d-flex align-items-center gap-3">
                {currentEvent && (
                  <div className="d-flex align-items-center text-white-50 small">
                    <i className="fas fa-calendar-check me-1"></i>
                    <span className="fw-semibold text-white">
                      {currentEvent.name}
                    </span>
                  </div>
                )}
                <button
                  className="btn btn-outline-light btn-sm rounded-pill px-3"
                  onClick={toggleLanguage}
                  title={t("common.language")}
                >
                  {i18n.language === "en" ? "ΕΛ" : "EN"}
                </button>
                <button
                  className="btn btn-outline-light btn-sm rounded-pill px-3"
                  onClick={handleLogout}
                >
                  <i className="fas fa-sign-out-alt me-1"></i>
                  {t("common.logout")}
                </button>
              </div>
            </div>
          </div>
        </nav>

        <div className="container py-4">
          <Routes>
            <Route
              path="/"
              element={
                <Dashboard
                  user={user}
                  userName={userName}
                  currentEvent={currentEvent}
                  onEventUpdate={setCurrentEvent}
                />
              }
            />
            <Route
              path="/guests"
              element={
                <GuestManager
                  user={user}
                  currentEvent={currentEvent}
                  onEventUpdate={setCurrentEvent}
                />
              }
            />
            <Route
              path="/tasks"
              element={
                <TaskManager
                  user={user}
                  currentEvent={currentEvent}
                  onEventUpdate={setCurrentEvent}
                />
              }
            />
            <Route
              path="/seating"
              element={
                <SeatingManager
                  user={user}
                  currentEvent={currentEvent}
                  onEventUpdate={setCurrentEvent}
                />
              }
            />
            <Route
              path="/report"
              element={<ReportExport user={user} currentEvent={currentEvent} />}
            />
            <Route path="/profile" element={<Profile user={user} onUserNameChange={setUserName} onLogout={handleLogout} />} />
            <Route path="/vendors" element={<Vendors />} />
            <Route path="/admin" element={<AdminVendors user={user} />} />
          </Routes>
        </div>
      </div>
      {showTutorial && <TutorialModal onDone={completeTutorial} />}
    </Router>
  );
}

export default App;
